import ApplicationCulture from '../config/ApplicationCulture.js';
import TypeHelper from './TypeHelper.js';
import NumberHelper from './NumberHelper.js';

const StringHelper = (function() {
    
    /* 
        TEST PATTERNS 
        - Don't use 'g' (global) options at the end of the pattern.
            If you use it, don't forget to reset the global state: 'myRegexObj.lastIndex = 0'.
    */
    const _testHtmlTagStartPattern = /^(<(html|body|div|h[123]|p|ul|ol|span|br|b|i|u|strike|form|table|img|section|strong|textarea|address|article|code|label|pre)>|<a href=")/;
    const _testHtmlEditorStartPattern = /^<(h[123]|p|ul|ol|br)>/;
    const _testOnlyDigits = /[^\d]+/;
    const _testSentenceEndPunctuation = / *[\.!]$/;

    /* REPLACEMENT PATTERNS */
    const _oneLinePattern = /(\r\n|\n)/g;
    const _unicodeSpacesPattern = /[\u00A0\u1680\u180E\u2000-\u200B\u202F\u205F\u3000\uFEFF]+/g;
    const _trimEmptyTagPattern = /(<(?<tag>p|h\d|b|i|u|strike|div)><\/\k<tag>>|<(?<list>ul|ol)><li><\/li><\/\k<list>>)/g;
    const _rangyPattern = /\<span id="selectionBoundary[_\d]+" class="rangySelectionBoundary"\>\&\#[\d]+;\<\/span\>/g
    const _nonDigitsPattern = /[^\d]+/g;

    /**
     * Remove os espaços em branco (32 \u0020 \x20 \040) do começo e fim de uma string.
     * @public
     * @function
     * @param {string} text A string a ser processada.
     * @returns {string} A string processada.
     */
    const TrimWhiteSpaces = (text) => {

        if ( !TypeHelper.IsString(text) ) return "";

        return text.replace(/(^\x20+|\x20+$)/g, "");
    };

    /**
     * Verifica se a string é nula, vazia ou possui apenas espaços em branco.
     * @public
     * @function
     * @param {string} value A string a ser verificada.
     * @returns {boolean} Se a string é nula, vazia ou possui apenas espaços em branco.
     */
    const IsNullOrEmpty = (value) => {
        return (
            !TypeHelper.IsString(value)
            || TrimWhiteSpaces(value) === ""
        );
    };

    /**
     * Verifica se a string é nula, vazia ou possui apenas qualquer tipo de espaço.
     * @public
     * @function
     * @param {string} value A string a ser verificada.
     * @returns {boolean} Se a string é nula, vazia ou possui apenas qualquer tipo de espaço.
     */
    const IsNullOrAnySpaces = (value) => {
        return (
            !TypeHelper.IsString(value)
            || value.trim() === ""
        );
    };

    /**
     * Remove todos os caracteres não numéricos de uma string.
     * @public
     * @function
     * @param {string} text - A string a ser processada.
     * @returns {string} A string processada.
     */
    const RemoveAllNonDigits = (text) => {

        if ( !TypeHelper.IsString(text) ) return text;

        _nonDigitsPattern.lastIndex = 0;

        text = text.replace(_nonDigitsPattern, "");

        _nonDigitsPattern.lastIndex = 0;

        return text;
    }

    /**
     * Remove todos os diacríticos de uma string. Diacríticos são marcas que podem ser adicionadas a letras 
     * para alterar sua pronúncia ou significado, como acentos, til, etc.
     * @public
     * @function
     * @param {string} text - A string a ser processada.
     * @returns {string} A string processada.
     */
    const RemoveDiacritics = (text) => {
            
        if ( IsNullOrEmpty(text) ) return "";

        const normalized = text.normalize('NFD');
        // \p{Mn} diacritical marks
        return normalized.replace(/\p{Mn}/gu, '');
    };

    /**
     * Adiciona caracteres à esquerda de uma string até que ela atinja o tamanho específicado.
     * @public
     * @function
     * @param {string} text - A string a ser processada.
     * @param {number} length - O tamanho desejado da string resultante.
     * @param {string} chr - O caractere a ser usado para preencher a string.
     * @returns {string} A string processada.
     */
    const PadLeft = (text, length = 2, chr = "0") => {
            
        if ( !TypeHelper.IsString(text) ) {
            
            text = NumberHelper.IsValid(text) ? text.toString() : "";
        }

        if ( !NumberHelper.IsValid(length) || length < 1) length = 0;

        if ( !TypeHelper.IsString(chr) ) chr = "";

        return text.padStart(length, chr);
    };

    /**
     * Adiciona caracteres à direita de uma string até que ela atinja o tamanho específicado.
     * @public
     * @function
     * @param {string} text - A string a ser processada.
     * @param {number} length - O tamanho desejado da string resultante.
     * @param {string} chr - O caractere a ser usado para preencher a string.
     * @returns {string} A string processada.
     */
    const PadRight = (text, length = 2, chr = "0") => {
        
        if ( !TypeHelper.IsString(text) ) {
            
            text = NumberHelper.IsValid(text) ? text.toString() : "";
        }

        if ( !NumberHelper.IsValid(length) || length < 1) length = 0;

        if ( !TypeHelper.IsString(chr) ) chr = "";

        return text.padEnd(length, chr);
    };

    //  CULTURE FORMAT - START >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
    //

    /**
     * Converte um valor em texto levando em consideração a cultura atual da aplicação.
     * @public
     * @function
     * @param {number} value Valor a ser convertido.
     * @param {number} decimalPlaces Quantidade de casas decimais.
     * @returns {string} Valor no formato da cultura atual da aplicação.
     */
    const NumberFormat = (value, decimalPlaces = 2) => {

        var valueNumber = NumberHelper.ToNumber(value, false);

        if ( isNaN(valueNumber) ) return "";

        decimalPlaces = NumberHelper.ToNumber(decimalPlaces, true);

        if ( isNaN(decimalPlaces) || decimalPlaces < 0 ) decimalPlaces = 0;

        return ApplicationCulture.CurrentCulture.NumberIntl.format(
            NumberHelper.Truncate(valueNumber, decimalPlaces)
        );
    };

    /**
     * Converte um valor monetário em texto levando em consideração a moeda atual da aplicação.
     * @public
     * @function
     * @param {number} value Valor monetário a ser convertido.
     * @returns {string} Valor monetário com o símbolo da moeda da cultura atual da aplicação.
     */
    const CurrencyFormat = (value) => {

        let valueNumber = NumberHelper.ToNumber(value, false);

        if ( isNaN(valueNumber) ) return "";

        return ApplicationCulture.CurrentCulture.CurrencyIntl.format(NumberHelper.Truncate(valueNumber, 2));
    };

    /**
     * Converte um valor booleano em uma string respeitando a cultura atual.
     * @public
     * @function
     * @param {boolean} value Booleano a ser convertido.
     * @param {boolean} upperCase Se a conversão deve ser em caixa alta.
     * @returns {string}
     */
    const BooleanToString = (value, upperCase = true) => {

        if ( !TypeHelper.IsBoolean(value) ) return "";

        if ( !TypeHelper.IsBoolean(upperCase) ) upperCase = true;

        switch ( ApplicationCulture.CurrentCulture.LCID ) {

            case ApplicationCulture.Brazil.LCID:
            {
                return (
                    value.valueOf() 
                        ? upperCase ? "SIM" : "sim"
                        : upperCase ? "NÃO" : "não"
                );
            }

            default:
            {
                return (
                    value.valueOf() 
                        ? upperCase ? "SIM" : "sim"
                        : upperCase ? "NÃO" : "não"
                );
            }
        }
    };

    //  CULTURE FORMAT - END <<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<
    
    const _helper = {
        TrimWhiteSpaces: TrimWhiteSpaces,
        IsNullOrEmpty: IsNullOrEmpty,
        IsNullOrAnySpaces: IsNullOrAnySpaces,
        RemoveAllNonDigits: RemoveAllNonDigits,
        RemoveDiacritics: RemoveDiacritics,
        PadLeft: PadLeft,
        PadRight: PadRight,
        NumberFormat: NumberFormat,
        CurrencyFormat: CurrencyFormat,
        BooleanToString: BooleanToString,
    };
 
    return Object.freeze(_helper);

})();

export default StringHelper;